import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  // iter27 — subscription snapshot used by Catalog / Dashboard / Pricing.
  const [subscription, setSubscription] = useState(null);

  async function refreshSubscription() {
    try {
      const res = await api.get("/catalog/me/subscription");
      setSubscription(res.data);
      return res.data;
    } catch {
      setSubscription(null);
      return null;
    }
  }

  useEffect(() => {
    (async () => {
      // Skip the /auth/me round-trip if there's no token at all — avoids
      // noisy 401s in the console on public pages (catalog, pricing).
      const hasToken = !!localStorage.getItem("access_token");
      if (!hasToken) {
        setUser(false);
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        await refreshSubscription();
      } catch {
        // Token is invalid/expired — clear it so we don't keep retrying
        localStorage.removeItem("access_token");
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.access_token) {
      localStorage.setItem("access_token", res.data.access_token);
    }
    setUser(res.data.user);
    await refreshSubscription();
    return res.data.user;
  }

  async function register(email, password, name) {
    const res = await api.post("/auth/register", { email, password, name });
    if (res.data.access_token) {
      localStorage.setItem("access_token", res.data.access_token);
    }
    setUser(res.data.user);
    await refreshSubscription();
    return res.data.user;
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (e) { void e; }
    localStorage.removeItem("access_token");
    setUser(false);
    setSubscription(null);
  }

  // Tier-aware helpers (iter27 — 3-tier model)
  const tier = user?.subscription_tier || subscription?.tier || "free";
  const isSubscribed = !!(user && (user.subscription_active || user.role === "admin"));
  const canUseAi = !!(subscription?.can_use_ai ?? isSubscribed);
  const canCreateProject = !!(subscription?.can_create_project ?? isSubscribed);
  const hadSubscription = !!(user?.had_subscription || subscription?.had_subscription);
  const cancelled = hadSubscription && !isSubscribed;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        setUser,
        subscription,
        refreshSubscription,
        // Derived flags
        tier,
        isSubscribed,
        canUseAi,
        canCreateProject,
        hadSubscription,
        cancelled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { formatApiErrorDetail };
